import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Button,
  Popconfirm,
  Row,
  Col,
  Form,
  Select,
  Space,
  Empty,
  Input,
  Tooltip,
  Divider,
  Typography,
  Tag,
  Steps,
  Badge,
} from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import Utilities from "../../../dependencies/custom/react-utilities";
import Settings from "../../../dependencies/custom/settings";
import useTable from "../../../hooks/table";
import useExcel from "../../../hooks/excel";
import CustomFunctions from "../../../dependencies/custom/custom-functions";
import ValuesStore from "../../../store/values-store";
import useDrawer from "../../../hooks/drawer";
import {
  buildPreschoolLearningAreas,
  buildStudentCompetenceScores,
  competenceFieldKey,
} from "./nurseryAssessmentReviewUtils";

const { Title } = Typography;

const EXAM_PERIOD_OPTIONS = [
  { value: "MID", label: "MID" },
  { value: "EOT", label: "EOT" },
];

function withCacheBuster(url) {
  if (!url) return url;
  return `${url}${url.includes("?") ? "&" : "?"}v=${Date.now()}`;
}

function groupRowsByStudentInResponseOrder(rows = []) {
  const grouped = new Map();
  const sourceRows = Array.isArray(rows) ? rows : [];

  sourceRows.forEach((row) => {
    const studentID = row?.studentID;
    if (!grouped.has(studentID)) grouped.set(studentID, []);
    grouped.get(studentID).push(row);
  });

  return grouped;
}

const NurseryAssessmentReview = ({ title = "", isHeadTeacher = false }) => {
  const drawer = useDrawer();
  const valuesStore = ValuesStore();
  const [classID, setClassID] = useState();
  const [termID, setTermID] = useState();
  const [acadyrID, setAcadyrID] = useState();
  const [periodType, setPeriodType] = useState("EOT");

  const [classes, setClasses] = useState([]);
  const [terms, setTerms] = useState([]);
  const [acadyrs, setAcadyrs] = useState([]);
  const [streamID, setStreamID] = useState();
  const [streams, setStreams] = useState([]);
  const [assignments, setAssignments] = useState([]);

  const [selectedStudent, setSelectedStudent] = useState();
  const [remark, setRemark] = useState();
  const [remarksMap, setRemarksMap] = useState({});

  const location = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [subjects, setSubjects] = useState([]);
  const [data, setData] = useState([]);
  const assessments = ["BoT", "Mid", "IntExam", "ExtExam"];
  const excel = useExcel();

  const gradingRemarks = useMemo(() => {
    const storedGrades = valuesStore.getValue("preschool_grade_remark");
    return Array.isArray(storedGrades) ? storedGrades : [];
  }, []);
  const [learningAreas, setLearningAreas] = useState([]);

  const table = useTable(
    {
      pagination: {
        current: 1,
        pageSize: 200,
        hideOnSinglePage: true,
      },
    },
    undefined,
    undefined,
    undefined,
    undefined,
    "id",
    {},
    {},
  );

  const table2 = useTable(
    {
      pagination: {
        current: 1,
        pageSize: 20,
        hideOnSinglePage: true,
      },
    },
    undefined,
    undefined,
    undefined,
    undefined,
    "id",
    {},
    {},
  );

  const statusCounts = useMemo(() => {
    const counts = { pending: 0, approved: 0, published: 0 };
    data.forEach((record) => {
      const s = record?.status?.toLowerCase();
      if (s === "approved") counts.approved++;
      else if (s === "published") counts.published++;
      else counts.pending++;
    });
    return counts;
  }, [data]);

  function getStatusTag(status) {
    const s = status?.toLowerCase();
    if (s === "approved") return <Tag color="blue">Approved</Tag>;
    if (s === "published") return <Tag color="green">Published</Tag>;
    return <Tag color="default">Pending</Tag>;
  }

  const selectedPeriodLabel =
    EXAM_PERIOD_OPTIONS.find((option) => option.value === periodType)?.label ||
    periodType;
  const actionPrompt = (message = "Please confirm action") => (
    <Space direction="vertical" size={0}>
      <span>{message}</span>
      <span>Exam Period: {selectedPeriodLabel}</span>
    </Space>
  );

  function statusActions(record, iconOnly = true) {
    if (isHeadTeacher) {
      if (record?.status?.toLowerCase() === "approved") {
        return myPopConfirm(
          "Publish Report",
          actionPrompt("Please confirm action"),
          onSubmit,
          undefined,
          myButton(
            "Publish",
            "btn-success border-0",
            "fas fa-newspaper",
            iconOnly,
          ),
          record,
        );
      }
      if (record?.status?.toLowerCase() === "published") {
        return (
          <Space>
            <Tooltip placement="left" title="Download report card">
              <Button
                className="btn-info border-0"
                onClick={() => downloadReport(record)}
              >
                <i className="fas fa-download" />
              </Button>
            </Tooltip>
            <Tooltip
              placement="left"
              title="This action returns the report to its previous status"
            >
              {myPopConfirm(
                "Unpublish Report",
                actionPrompt("Please confirm action"),
                onSubmit,
                undefined,
                myButton(
                  "Unpublish",
                  "btn-warning border-0",
                  "fas fa-unlink",
                  iconOnly,
                ),
                [record],
                "Approved",
              )}
            </Tooltip>
            <Tooltip
              placement="left"
              title="This action returns the report to the class teacher for a reapproval"
            >
              {myPopConfirm(
                "Reset Report",
                actionPrompt("Please confirm action"),
                onReset,
                undefined,
                myButton(
                  "Reset",
                  "btn-danger border-0",
                  "fas fa-eraser",
                  iconOnly,
                ),
                [record],
              )}
            </Tooltip>
          </Space>
        );
      }
      if (!record?.status) return <Tag color="default">Awaiting approval</Tag>;
      return <Tag>{record.status}</Tag>;
    }

    // Class teacher
    if (!record?.status) {
      return myPopConfirm(
        "Approve Report",
        actionPrompt("Please confirm action"),
        onSubmit,
        undefined,
        myButton(
          "Approve",
          "btn-success border-0",
          "fas fa-check-double",
          iconOnly,
        ),
        record,
      );
    }
    if (record?.status?.toLowerCase() === "approved") {
      return (
        <Tag color="blue" icon={<i className="fas fa-check me-1" />}>
          Approved
        </Tag>
      );
    }
    return getStatusTag(record?.status);
  }

  function updateRemark(studentId, value) {
    setRemarksMap((prev) => ({ ...prev, [studentId]: value }));
  }

  function viewReport(record) {
    setSelectedStudent(record);
    const competenceColumns = learningAreas.map((area) => ({
      title: area?.columnName,
      key: `area-${area?.columnID}`,
      children: area.competences.map((competence) => ({
        title: competence?.statement,
        dataIndex: competenceFieldKey(competence?.competenceID),
        key: competenceFieldKey(competence?.competenceID),
        width: 180,
      })),
    }));

    table2.setColumns(competenceColumns);
    table2.setData([record]);
    drawer.setOpen(true);
    drawer.setPlacement("right");
    drawer.setWidth(700);
    setRemark(remarksMap[record?.id] || record?.teacherRemark || "");
    drawer.setTitle(`Academic report for ${record?.name}`);
  }

  const columns = useMemo(
    () => [
      {
        title: "Student ID",
        dataIndex: "id",
        key: "id",
      },
      {
        title: "Student",
        key: "name",
        dataIndex: "name",
        onCell: () => ({
          style: { whiteSpace: "normal", wordWrap: "break-word" },
        }),
        width: 150,
        render: (v, record) => (
          <Tooltip title="View report">
            <Button onClick={() => viewReport(record)} type="link">
              {v}
            </Button>
          </Tooltip>
        ),
      },
      ...learningAreas.map((area) => ({
        title: area?.columnName,
        key: `area-${area?.columnID}`,
        children: area.competences.map((competence) => ({
          title: competence?.statement,
          key: competenceFieldKey(competence?.competenceID),
          dataIndex: competenceFieldKey(competence?.competenceID),
          width: 180,
          onCell: () => ({
            style: { whiteSpace: "normal", wordWrap: "break-word" },
          }),
        })),
      })),
      {
        title: "Status",
        key: "status",
        width: 100,
        render: (_, record) => getStatusTag(record?.status),
      },
      {
        title: "Remarks",
        key: "remarks",
        width: 150,
        onCell: () => ({
          style: { whiteSpace: "normal", wordWrap: "break-word" },
        }),
        render: (_, record) => (
          <Input.TextArea
            autoSize
            placeholder="Enter remarks (saved on approve/publish)"
            value={remarksMap[record?.id] ?? record?.teacherRemark ?? ""}
            onChange={(e) => updateRemark(record?.id, e.target.value)}
          />
        ),
      },
      {
        title: "Action",
        key: "action",
        fixed: "end",
        width: 120,
        render: (_, record) => statusActions(record),
      },
    ],
    [learningAreas, data, remarksMap],
  );

  async function getClassScores() {
    if (classID && streamID && termID && acadyrID && periodType) {
      table.setLoading(true);
      const query = `period_type=${periodType}&periodType=${periodType}`;
      const res = await Utilities.fetchWithAuth(
        "GET",
        `${Settings?.urls?.academics?.preschool_class_scores}/${classID}/${streamID}/${acadyrID}/${termID}?${query}`,
        undefined,
        navigate,
      );
      if (res?.ok) {
        transformData(res?.data);
      } else {
        if (res?.msg) Utilities.showNotification(undefined, res?.msg);
      }
      table.setLoading(false);
    } else {
      Utilities.showNotification(
        undefined,
        "Please select class, stream, academic year, term and exam period",
      );
    }
  }

  function transformData(dbData) {
    const res = groupRowsByStudentInResponseOrder(dbData);
    let final = [];
    const newRemarksMap = {};

    setLearningAreas(buildPreschoolLearningAreas(dbData));

    for (let studentDetails of res.values()) {
      const firstEntry = studentDetails?.[0] || {};
      const remarkValue = isHeadTeacher
        ? firstEntry?.headTeacherRemarks
        : firstEntry?.classTeacherRemarks;
      let obj = {
        tableRowId: firstEntry?.assessmentID,
        id: firstEntry?.studentID,
        name: CustomFunctions.formatStudentName(firstEntry),
        remarks: remarkValue,
        status: firstEntry?.assessmentStatus,
        teacherRemark: firstEntry?.classTeacherRemarks,
      };

      if (remarkValue) {
        newRemarksMap[firstEntry?.studentID] = remarkValue;
      }

      Object.assign(
        obj,
        buildStudentCompetenceScores(studentDetails, gradingRemarks),
      );
      final.push(obj);
    }
    setRemarksMap((prev) => ({ ...newRemarksMap, ...prev }));
    setData(final);
  }

  useEffect(() => {
    table.setColumns(columns);
    table.setData(data);
  }, [data, columns]);

  useEffect(() => {
    reset();
    if (isHeadTeacher) {
      CustomFunctions.getParams(
        valuesStore,
        true,
        setClasses,
        setAcadyrs,
        setTerms,
        false,
      );
    } else {
      fetchAssignments();
      CustomFunctions.getParams(
        valuesStore,
        false,
        () => {},
        setAcadyrs,
        setTerms,
        false,
      );
    }
  }, [location.pathname]);

  async function fetchAssignments() {
    const res = await Utilities.fetchWithAuth(
      "GET",
      Settings?.urls?.academics?.class_subjects_v2,
      undefined,
      navigate,
    );
    if (res?.ok) {
      const data = Array.isArray(res?.data)
        ? res?.data
        : res?.data?.results || [];
      setAssignments(data);
      const seen = new Set();
      const classOpts = data
        .filter((a) => a?.classGroup?.toLowerCase() === "preschool")
        .filter((a) => {
          if (seen.has(a.classID)) return false;
          seen.add(a.classID);
          return true;
        })
        .map((a) => ({ value: a.classID, label: a.className }));
      setClasses(classOpts);
    }
  }

  useEffect(() => {
    if (!classID) {
      setStreams([]);
      setStreamID(undefined);
      return;
    }
    if (isHeadTeacher) {
      const allStreams = valuesStore.getValue("streams") || [];
      setStreams(allStreams.map((s) => ({ value: s.id, label: s.name })));
    } else {
      const seen = new Set();
      const streamOpts = assignments
        .filter((a) => a.classID === classID)
        .filter((a) => {
          if (seen.has(a.streamID)) return false;
          seen.add(a.streamID);
          return true;
        })
        .map((a) => ({ value: a.streamID, label: a.streamName }));
      setStreams(streamOpts);
    }
    setStreamID(undefined);
  }, [classID]);

  function reset() {
    table.setData([]);
    setData([]);
    setAcadyrID(undefined);
    setTermID();
    setPeriodType("EOT");
    setClassID();
    setStreamID(undefined);
    setStreams([]);
    setLearningAreas([]);
    setRemarksMap({});
    form.resetFields();
  }

  function myPopConfirm(
    title,
    description,
    onConfirm,
    onCancel,
    child,
    tableRow,
    status,
  ) {
    return (
      <Popconfirm
        title={title}
        description={description}
        onConfirm={() => onConfirm(tableRow, status)}
        onCancel={onCancel}
        okText="Yes"
        cancelText="No"
      >
        {child}
      </Popconfirm>
    );
  }

  function myButton(title, cssClasses, icon, iconOnly = false) {
    return iconOnly ? (
      <Button className={cssClasses}>
        <i className={icon} />
      </Button>
    ) : (
      <Button className={cssClasses} icon={<i className={icon} />}>
        {title}
      </Button>
    );
  }

  async function downloadMergedReport() {
    if (!classID || !streamID || !acadyrID || !termID || !periodType) {
      Utilities.showNotification(
        undefined,
        "Select class, stream, academic year, term and period first",
      );
      return;
    }
    const url = `${Settings?.urls?.academics?.report_batch}/${classID}/${streamID}/${acadyrID}/${termID}?periodType=${periodType}`;
    try {
      const accessToken = Utilities.getCookie("access");
      const res = await fetch(url, {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
      if (!res.ok) {
        Utilities.showNotification(undefined, "Report not available");
        return;
      }
      const blob = await res.blob();
      const disposition = res.headers.get("content-disposition") || "";
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const filename = match
        ? match[1]
        : `report_${classID}_${streamID}_${acadyrID}_${termID}_${periodType}.pdf`;
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      Utilities.showNotification(undefined, "Failed to download report");
    }
  }

  async function downloadReport(record) {
    const url = `${Settings?.urls?.academics?.student_academic_report}/${record?.id}?acadyrID=${acadyrID}&termID=${termID}&periodType=${periodType}`;
    const res = await Utilities.fetchWithAuth("GET", url, undefined, navigate);
    // Backend serializes with many=True, so res.data is an array of reports.
    const reportFile = Array.isArray(res?.data)
      ? res.data[0]?.reportFile
      : res?.data?.reportFile;
    if (res?.ok && reportFile) {
      window.open(withCacheBuster(reportFile), "_blank");
    } else {
      Utilities.showNotification(undefined, res?.msg || "Report not available");
    }
  }

  function onExcelExport() {
    excel.exportXLSX(
      table.data.length ? Object.keys(table.data[0]) : [],
      table.data,
      "sheet1",
      `${Utilities.formatDate(new Date()?.toDateString())}_bulk_scores.xlsx`,
    );
  }

  const onSubmit = async (v, status) => {
    if (!acadyrID || !termID) {
      Utilities.showNotification(
        undefined,
        "Please set the class, academic year and term parameters",
      );
      return;
    }

    let final;

    if (v && Array.isArray(v)) {
      const record = v[0];
      final = [
        {
          studentID: record?.id,
          acadyrID,
          termID,
          periodType,
          [isHeadTeacher ? "headTeacherRemarks" : "teacherRemarks"]:
            remarksMap[record?.id] || record?.remarks,
          status: status || (isHeadTeacher ? "Published" : "Approved"),
        },
      ];
    } else if (v && Utilities.isPlainObject(v)) {
      final = [
        {
          studentID: v?.id,
          acadyrID,
          termID,
          periodType,
          [isHeadTeacher ? "headTeacherRemarks" : "teacherRemarks"]:
            remarksMap[v?.id] || v?.remarks,
          status: status || (isHeadTeacher ? "Published" : "Approved"),
        },
      ];
    } else {
      // Bulk action — filter by eligible status
      const targetStatus = status || (isHeadTeacher ? "Published" : "Approved");
      const eligible = data.filter((record) => {
        const s = record?.status?.toLowerCase();
        if (isHeadTeacher && targetStatus === "Published")
          return s === "approved";
        if (isHeadTeacher && targetStatus === "Approved")
          return s === "published";
        if (!isHeadTeacher) return !record?.status;
        return false;
      });

      if (!eligible.length) {
        Utilities.showNotification(
          undefined,
          "No eligible records for this action",
        );
        return;
      }

      final = eligible.map((record) => ({
        studentID: record?.id,
        acadyrID,
        termID,
        periodType,
        [isHeadTeacher ? "headTeacherRemarks" : "teacherRemarks"]:
          remarksMap[record?.id] || record?.remarks,
        status: targetStatus,
      }));
    }

    table.setLoading(true);
    const res = await Utilities.fetchWithAuth(
      "POST",
      Settings?.urls?.academics?.student_assessment,
      final,
      navigate,
    );
    if (res?.ok) {
      Utilities.showNotification(
        undefined,
        "Operation successful",
        "text-success",
      );
      getClassScores();
      drawer.setOpen(false);
    } else {
      Utilities.showNotification(undefined, res?.msg);
    }
    table.setLoading(false);
  };

  async function onReset(records) {
    const d = Utilities.createArrayFromSingleObjectKey(
      Array.isArray(records) ? records : data.filter((r) => r?.status),
      "tableRowId",
    )?.filter((v) => v);

    if (!d?.length) {
      Utilities.showNotification(undefined, "No eligible records to reset");
      return;
    }

    table.setLoading(true);
    const res = await Utilities.fetchWithAuth(
      "DELETE",
      Settings?.urls?.academics?.student_assessment_del,
      { id: d },
      navigate,
    );
    if (res?.ok) {
      Utilities.showNotification(
        undefined,
        "Operation successful",
        "text-success",
      );
      getClassScores();
    } else {
      Utilities.showNotification(undefined, res?.msg);
    }
    table.setLoading(false);
  }

  function changeParam(setState, value) {
    table.setData([]);
    form.resetFields();
    setData([]);
    setLearningAreas([]);
    setRemarksMap({});
    setState(value);
  }

  function mySelect(
    options,
    setState,
    stateValue,
    placeholder = "",
    width = 120,
    props = {},
  ) {
    return (
      <Select
        placeholder={placeholder}
        style={{ width }}
        onChange={(v) => changeParam(setState, v)}
        value={stateValue}
        options={options}
        {...props}
      />
    );
  }

  function setMyRemark(val, details) {
    setRemark(val);
    updateRemark(details?.id, val);
  }

  const workflowStep = isHeadTeacher ? 1 : 0;

  return (
    <>
      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <Card
            title={
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <span>{title}</span>
                <Steps
                  size="small"
                  current={workflowStep}
                  items={[
                    { title: "Class Teacher Review" },
                    { title: "Head Teacher Publication" },
                  ]}
                  style={{ maxWidth: 400 }}
                />
              </Space>
            }
            bordered={false}
            extra={
              <div className="my-3">
                <Space wrap>
                  {mySelect(
                    classes,
                    setClassID,
                    classID,
                    "Please select class",
                    200,
                  )}
                  {mySelect(
                    streams,
                    setStreamID,
                    streamID,
                    "Please select stream",
                    180,
                  )}
                  {mySelect(acadyrs, setAcadyrID, acadyrID, "Academic year")}
                  {mySelect(terms, setTermID, termID, "Term")}
                  {mySelect(
                    EXAM_PERIOD_OPTIONS,
                    setPeriodType,
                    periodType,
                    "Exam period",
                    130,
                  )}
                  <Button
                    onClick={getClassScores}
                    className="btn-secondary border-0"
                    icon={<i className="fas fa-paper-plane" />}
                  >
                    Fetch Report
                  </Button>

                  {isHeadTeacher ? (
                    <Space wrap>
                      {myPopConfirm(
                        "Publish Reports",
                        actionPrompt(
                          `Publish ${statusCounts.approved} approved report(s)?`,
                        ),
                        onSubmit,
                        undefined,
                        <Badge
                          count={statusCounts.approved}
                          size="small"
                          offset={[5, 0]}
                        >
                          {myButton(
                            "Publish All",
                            `btn-success border-0${!statusCounts.approved ? " opacity-50" : ""}`,
                            "fas fa-newspaper",
                          )}
                        </Badge>,
                      )}
                      <Tooltip
                        placement="left"
                        title="This action returns the reports to their previous status"
                      >
                        {myPopConfirm(
                          "Unpublish Reports",
                          actionPrompt(
                            `Unpublish ${statusCounts.published} published report(s)?`,
                          ),
                          onSubmit,
                          undefined,
                          <Badge
                            count={statusCounts.published}
                            size="small"
                            offset={[5, 0]}
                          >
                            {myButton(
                              "Unpublish All",
                              `btn-warning border-0${!statusCounts.published ? " opacity-50" : ""}`,
                              "fas fa-unlink",
                            )}
                          </Badge>,
                          undefined,
                          "Approved",
                        )}
                      </Tooltip>
                      <Tooltip
                        placement="left"
                        title="This action returns the report to the class teacher for a reapproval"
                      >
                        {myPopConfirm(
                          "Reset Reports",
                          actionPrompt("Reset all reports?"),
                          onReset,
                          undefined,
                          myButton(
                            "Reset All",
                            "btn-danger border-0",
                            "fas fa-eraser",
                          ),
                          data,
                        )}
                      </Tooltip>
                      <Tooltip
                        placement="left"
                        title="Download a merged report for the selected class & stream"
                      >
                        <Button
                          className="btn-info border-0"
                          icon={<i className="fas fa-download" />}
                          onClick={downloadMergedReport}
                        >
                          Download
                        </Button>
                      </Tooltip>
                    </Space>
                  ) : (
                    myPopConfirm(
                      "Approve Reports",
                      actionPrompt(
                        `Approve ${statusCounts.pending} pending report(s)?`,
                      ),
                      onSubmit,
                      undefined,
                      <Badge
                        count={statusCounts.pending}
                        size="small"
                        offset={[5, 0]}
                      >
                        {myButton(
                          "Approve All",
                          `btn-success border-0${!statusCounts.pending ? " opacity-50" : ""}`,
                          "fas fa-check-double",
                        )}
                      </Badge>,
                    )
                  )}
                </Space>
              </div>
            }
          >
            <Row gutter={[24, 24]}>
              <Col xs={24}>
                <div className="assessment_review">
                  {table?.data?.length ? (
                    <Form form={form} layout="vertical">
                      {table.tableWithHeaderFooter(
                        CustomFunctions.tblCaption,
                        CustomFunctions.tblCaption,
                        { bordered: true, scroll: { x: "max-content" } },
                      )}
                    </Form>
                  ) : (
                    <Empty />
                  )}
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
      {drawer.drawerJSX(
        1000,
        <>
          <div>
            <div className="mb-3">
              <Title level={4}>
                Academic Year:{" "}
                {acadyrs?.find((v) => v?.value == acadyrID)?.label}
              </Title>
              <Title level={4}>
                Term: {terms?.find((v) => v?.value == termID)?.label}
              </Title>
              <Title level={4}>Exam Period: {selectedPeriodLabel}</Title>
              {selectedStudent && (
                <div className="mb-2">
                  <span className="fw-bold me-2">Status:</span>
                  {getStatusTag(selectedStudent?.status)}
                </div>
              )}
              {table2.table}
            </div>
            <Divider>Take action</Divider>
            <div>
              <div className="mb-2">
                {selectedStudent && statusActions(selectedStudent, false)}
              </div>
              <label className="fw-bold">Remarks:</label>
              <Input.TextArea
                autoSize
                placeholder="Enter remarks (saved on approve/publish)"
                value={remark}
                onChange={(e) => setMyRemark(e.target.value, selectedStudent)}
              />
            </div>
          </div>
        </>,
      )}
    </>
  );
};

export default NurseryAssessmentReview;
